<template>
  <div class="relative w-full flex flex-col px-5 pt-5" style="height: calc(100vh - 150px);">
    <div class="flex justify-start items-center gap-3 mb-5">
      <button class="secondary-button flex gap-1 items-center pl-2 pr-3 py-1.5 rounded-md"
      @click="openNewReportModal('top-performing')">
        <CreatePerformanceReportItemIcon class="text-icon-normal"/>
        <BaseText type="label" size="sm" class="text-text-muted">
          Create Top Performing Report
        </BaseText>
      </button>
      <button class="secondary-button flex gap-1 items-center pl-2 pr-3 py-1.5 rounded-md"
      @click="openNewReportModal('comparative')">
        <LensCreateComparisonReportIcon class="text-icon-normal" />
        <BaseText type="label" size="sm" class="text-text-muted">
          Create Comparative Report
        </BaseText>
      </button>
    </div>
    <!-- Report Table Header -->
    <div class="report-grid-template h-9 rounded-lg bg-neutral-25">
      <!-- Name Column / Select All -->
      <div class="w-full flex items-center gap-3.5 pl-3 pr-4 py-1.5">
        <input type="checkbox" class="checkbox-input" :disabled="disableToggleAll"
        :checked="selectedRows.length > 0 && selectedRows.length === reports.length"
        @change="toggleAllRows(false)">
        <BaseText type="body" size="sm" class="text-text-normal">
          Name
        </BaseText>
      </div>
      <!-- Last Updated Column -->
      <BaseText type="body" size="sm" class="text-text-normal flex items-center px-3 py-2">
        Last Updated
      </BaseText>
      <!-- Created Column -->
      <BaseText type="body" size="sm" class="text-text-normal flex items-center px-3 py-2">
        Created
      </BaseText>
      <!-- Type Column -->
      <BaseText type="body" size="sm" class="text-text-normal flex items-center px-3 py-2">
        Type
      </BaseText>
    </div>
    <!-- Report Table Body -->
    <div class="reports-container flex-grow w-full py-3 overflow-y-auto min-h-0">
      <!-- Loading State -->
      <div v-if="loading" class="w-full flex items-center justify-center h-48">
        <BaseLoadingSpinnerCircle class="text-text-disabled" :width="32" :height="32" :duration="1.2" />
      </div>
      <!-- No Reports State -->
      <div v-else-if="!reports?.length" class="w-full flex items-center justify-center h-48 fade-in">
        <div class="flex flex-col bg-neutral-malpha-300 py-4 px-4 rounded-lg text-center"
        style="max-width: 100%">
          <BaseText type="label" size="sm" class="text-white">
            No Reports Found
          </BaseText>
          <BaseText type="body" size="sm" class="text-neutral-alpha-650 mt-1.5">
            We couldn't find any reports for this Lens.
          </BaseText>
          <BaseText type="body" size="sm" class="text-neutral-alpha-650 mt-0.5">
            Try creating a new report!
          </BaseText>
        </div>
      </div>
      <!-- Reports List -->
      <div v-else class="relative w-full flex flex-col gap-1.5 pb-8">
        <div v-for="report in reports" :key="report.id" class="report-grid-template rounded-lg transition-colors duration-100 cursor-pointer fade-in"
        :class="expandedReportOptions === report.id ? 'bg-neutral-25' : 'hover:bg-neutral-25'"
        @click="navToReport(report.id)">
          <!-- Name / Select -->
          <div class="w-full flex items-center gap-1.5 pl-1.5 py-1.5 pr-4 min-w-0">
            <input :ref="`checkbox-${report.id}`" :checked="isRowSelected(report.id)" class="checkbox-input ml-1.5 mr-2 flex-shrink-0" type="checkbox"
            @click.stop @change="selectRow(report)" @mouseenter="showTooltip(report.id, true)" @mouseleave="showTooltip(report.id, false)">
            <ReportIcon v-if="report.type === 'top-performing'" class="text-icon-normal flex-shrink-0"/>
            <ComparativeIcon v-else class="text-icon-normal flex-shrink-0"/>
            <BaseText type="label" size="sm" class="text-text-muted flex-grow truncate">
              {{ report.name }}
            </BaseText>
          </div>
          <!-- Last Updated -->
          <div class="flex items-center px-3 py-2">
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ report.updated_at ? formatDate(report.updated_at) : formatDate(report.created_at) }}
            </BaseText>
          </div>
          <!-- Created -->
          <div class="flex items-center gap-2 px-3 py-2 min-w-0">
            <img v-if="getReportAuthorAvatar(report)"
            :src="getReportAuthorAvatar(report)" class="w-5 h-5 rounded-full flex-shrink-0">
            <div v-else class="w-5 h-5 rounded-full flex items-center justify-center bg-neutral-50 flex-shrink-0">
              <span class="text-text-muted text-label-xs font-medium">
                {{ getInitials(getReportAuthorName(report)) }}
              </span>
            </div>
            <BaseText type="label" size="sm" class="text-text-muted flex-shrink truncate">
              {{ getReportAuthorName(report) }}
              <!-- This is a very long name that will be truncated -->
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal flex-shrink-0">
              {{ formatDate(report.created_at) }}
            </BaseText>
          </div>
          <!-- Type / Options Menu -->
          <div class="w-full flex items-center pl-3 pr-1.5 py-1.5 min-w-0">
            <BaseText type="body" size="sm" class="flex-grow text-text-muted truncate">
              {{ report.type === 'top-performing' ? 'Top Performing' : 'Comparative' }}
            </BaseText>
            <div class="relative h-6 w-6">
              <!-- Options Menu Button -->
              <button @click.stop="expandedReportOptions = expandedReportOptions === report.id ? null : report.id"
              class="options-menu-button rounded-md p-0.5"
              :class="expandedReportOptions === report.id ? 'active' : 'inactive'">
                <KebabMenuIcon class="text-icon-normal" />
              </button>
              <!-- Options Dropdown -->
              <transition>
                <div v-if="expandedReportOptions === report.id" class="options-dropdown flex flex-col gap-0.5 p-0.5 rounded-lg bg-white"
                v-on-clickaway="() => expandedReportOptions = null">
                  <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                  @click.stop.prevent="expandedReportOptions = null, handleDuplicateReport(report.id)">
                    <DuplicateIcon class="transition-colors text-icon-normal group-hover:text-icon-muted" />
                    <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-text-muted">
                      Duplicate
                    </BaseText>
                  </button>
                  <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                  @click.stop.prevent="expandedReportOptions = null, handleDeleteModalView(report.id)">
                    <DeleteIcon class="transition-colors text-icon-normal group-hover:text-icon-muted" />
                    <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-text-muted">
                      Delete
                    </BaseText>
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
        <infinite-loading
          v-if="reports.length && !loading"
          :identifier="infiniteId"
          @infinite="getMoreReports"
        >
          <div slot="spinner" class="w-full flex items-center justify-center h-7">
            <BaseLoadingSpinnerCircle class="text-text-disabled" :width="28" :height="28" :duration="1.2" />
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
      </div>
    </div>

    <!-- Floating Bulk Actions -->
    <div v-if="selectedRows.length > 0"
    :class="{'collapsed': getSidebarCollapsed}" :style="{'--collapse-transition-duration': `${getSidebarCollapseDuration}ms`}"
    class="fixed bottom-8 left-1/2 transform -translate-x-1/2 sidebar-offset p-2 bg-display-muted shadow-lg z-50"
    style="background-color: rgba(6, 7, 16, 0.88); border-radius: 14px;">
      <div class="flex justify-between items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none" class="-mr-0.5">
          <path d="M11.2762 15.7367H15.7387M15.4746 5.59141L14.409 4.52578C13.9111 4.02786 13.1038 4.02786 12.6059 4.52578L4.63711 12.4946C4.398 12.7336 4.26367 13.0579 4.26367 13.3961V15.7367H6.6043C6.94245 15.7367 7.26675 15.6024 7.50586 15.3633L15.4746 7.39453C15.9725 6.89661 15.9725 6.08933 15.4746 5.59141Z"
          stroke="white" style="stroke:white;stroke-opacity:1;" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <BaseText type="label" size="sm" class="text-white">
          Bulk Edit Reports
        </BaseText>
        <BaseText type="body" size="sm" class="text-white mr-2">
          {{ selectedRows.length }} Selected
        </BaseText>

        <!-- Cancel Actions -->
        <button class="flex items-center gap-1.5 px-3 py-1.5 text-white custom-cancel-button"
        @click="clearSelection">
          <BaseText type="label" size="sm" class="text-white">
            Cancel
          </BaseText>
        </button>

        <!-- Duplicate Actions -->
        <button class="flex items-center gap-1.5 px-3 py-1.5 text-white custom-duplicate-button"
        @click="handleDuplicateReports">
          <BaseText type="label" size="sm" class="text-white">
            Duplicate
          </BaseText>
          <DuplicateIcon class="text-white" />
        </button>

        <!-- Delete Actions -->
        <button class="flex items-center gap-1.5 px-3 py-1.5 text-white custom-button custom-delete-button"
        @click="handleBulkDelete">
          <BaseText type="label" size="sm" class="text-secondary-red-50">
            Delete
          </BaseText>
          <DeleteIcon class="text-secondary-red-50" />
        </button>
      </div>
    </div>

    <!-- Modals -->
    <CreateLensReportModal
      v-if="show.createReportModal"
      :lens-id="lensId"
      :pre-selected-report-type="preSelectedReportType"
      @close="show.createReportModal = false"
    />
    <ConfirmDeleteModal
      v-if="show.deleteReportModal"
      :delete-title="`Are you sure you want to delete ${selectedRows.length > 1 ? 'these reports' : 'this report'}?`"
      delete-description="Once deleted, reports can not be recovered."
      :loading="loadingDelete"
      @close="handleDeleteModalView()"
      @confirm="handleReportDelete"
    />
    <DuplicateReportModal
      v-if="show.duplicateReportModal"
      :report="currentReportForDuplicate"
      :report-type="currentReportForDuplicate.type"
      @close="show.duplicateReportModal = false"
      @duplicated="updateReportsLocal($event)"
    />
  </div>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading'
import { mapActions, mapGetters } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'

import LensAPI from '@/api/lens'
import TablePagination from '../../components/lens/table/TablePagination.vue'
import CreateLensReportModal from '../../components/lens/CreateLensReportModal.vue'
import ConfirmDeleteModal from '../../components/globals/ConfirmDeleteModal.vue'
import BarGraphIcon from '../../components/globals/Icons/LensIcons/graphs/BarGraphIcon.vue'
import KebabMenuIcon from '../../components/globals/Icons/KebabMenuIcon.vue'
import DeleteIcon from '../../components/globals/Icons/DeleteIcon.vue'
import BaseLoadingSpinnerCircle from '../../components/globals/BaseLoadingSpinnerCircle.vue'
import DuplicateIcon from '../../components/globals/Icons/DuplicateIcon.vue'
import ReportIcon from '../../components/globals/Icons/LensIcons/ReportIcon.vue'
import DuplicateReportModal from '../../components/lens/report/DuplicateReportModal.vue'
import ComparativeIcon from '../../components/globals/Icons/SidebarIcons/ComparativeIcon.vue'
import CreatePerformanceReportItemIcon from '../../components/globals/Icons/SidebarIcons/dropdowns/CreatePerformanceReportItemIcon.vue'
import LensCreateComparisonReportIcon from '../../components/globals/Icons/LensCreateComparisonReportIcon.vue'

export default {
  name: 'LensDashboardReportView',
  mixins: [clickaway],
  components: {
    InfiniteLoading,
    TablePagination,
    CreateLensReportModal,
    ConfirmDeleteModal,
    BarGraphIcon,
    KebabMenuIcon,
    DeleteIcon,
    BaseLoadingSpinnerCircle,
    DuplicateIcon,
    ReportIcon,
    DuplicateReportModal,
    ComparativeIcon,
    CreatePerformanceReportItemIcon,
    LensCreateComparisonReportIcon
  },
  props: {
    currentLens: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      lensId: this.$route.params.lensId,
      reports: [],
      selectedRows: [],
      loading: false,
      loadingDelete: false,
      expandedReportOptions: null,
      reportToDelete: null,
      show: {
        createReportModal: false,
        deleteReportModal: false,
        duplicateReportModal: false
      },
      headers: [
        { label: 'Name', span: 4 },
        { label: 'Last Updated', span: 3 },
        { label: 'Created', span: 4 },
        { label: 'Type', span: 3 }
      ],
      tablePagination: {
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 20,
        currentPage: 1
      },
      infiniteId: +new Date(),
      currentReportForDuplicate: null,
      preSelectedReportType: null
    }
  },
  watch: {
    getPendingLocalReportUpdate (newUpdate) {
      // If a report was updated in the sidebar, we need to propogate those changes here
      if (newUpdate?.id && newUpdate?.data) {
        // Update the report in state if it has the same id as the update
        const updateReports = this.reports.map(report => (
          report.id === newUpdate.id ? { ...report, ...newUpdate.data } : report
        ))
        this.reports = updateReports
      } else if (newUpdate?.id && newUpdate?.isDeleted) {
        // Remove the deleted report from local state
        this.reports = this.reports.filter(report => report.id !== newUpdate.id)
      }
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getPendingLocalReportUpdate']),
    ...mapGetters('MiscModule', ['getSidebarCollapsed', 'getSidebarCollapseDuration']),
    disableToggleAll () {
      return false
    },
    preventRowAdd () {
      return false
    }
  },
  async mounted () {
    this.fetchReports()
  },
  methods: {
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    async fetchReports (isPaginationRequest = false) {
      if (!isPaginationRequest) {
        this.loading = true
        this.tablePagination.currentPage = 1
      }
      try {
        const response = await LensAPI.LensReport.getAll({
          lens_id: this.lensId,
          page: this.tablePagination.currentPage,
          limit: this.tablePagination.itemsPerPage,
          include_meta: true
        })
        this.reports = isPaginationRequest ? [...this.reports, ...response.data] : response.data

        // Update pagination if headers are available
        if (!isPaginationRequest && response.pages && response.total) {
          this.tablePagination.totalItems = response.total
          this.tablePagination.totalPages = response.pages
        }
      } catch (error) {
        console.error('Error fetching reports:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to load reports'
        })
      } finally {
        this.loading = false
      }
    },
    async getMoreReports ($state) {
      if (!this.lensId || this.tablePagination.currentPage >= this.tablePagination.totalPages) {
        if ($state?.complete) $state.complete()
        return
      }
      this.tablePagination.currentPage++
      await this.fetchReports(true)
      this.infiniteId++
      if (this.reports.length >= this.tablePagination.totalItems) {
        if ($state?.complete) $state.complete()
      } else {
        if ($state?.loaded) $state.loaded()
      }
    },
    handleDuplicateReport (reportId) {
      this.currentReportForDuplicate = this.reports.find(report => report.id === reportId)
      this.show.duplicateReportModal = true
    },
    handleDuplicateReports () {
      if (this.selectedRows.length === 0) return

      // Show loading notification
      this.$showAlert({
        type: 'info',
        message: `Duplicating ${this.selectedRows.length} report${this.selectedRows.length > 1 ? 's' : ''}...`
      })

      // Process all reports in sequence
      this.bulkDuplicateReports(this.selectedRows)
        .then(results => {
          // Show success message
          this.$showAlert({
            type: 'success',
            message: `Successfully duplicated ${results.length} report${results.length > 1 ? 's' : ''}`
          })
          // Update the local reports list
          this.reports = [...results, ...this.reports]
          // Clear selection
          this.selectedRows = []
          // Update the sidebar
          this.fetchSidebarLenses()
        })
        .catch(error => {
          console.error('Error duplicating reports:', error)
          this.$showAlert({
            type: 'error',
            message: 'Failed to duplicate some reports'
          })
        })
    },
    async bulkDuplicateReports (reports) {
      const results = []

      for (const report of reports) {
        try {
          // Create duplicate report data object
          const duplicateReport = {
            name: `${report.name} (Copy)`,
            selected_ad_accounts: report.selected_ad_accounts ?? null,
            description: report.description,
            lens_id: this.lensId, // Use current lens for all duplicates
            pinned_columns: report.pinned_columns,
            filters: report.filters,
            selected_columns: report.selected_columns,
            preset_id: report.preset_id,
            selected_graph_rows: report.selected_graph_rows,
            sorted_column: report.sorted_column,
            table_config: report.table_config,
            group_by: report.group_by,
            type: report.type,
            table_columns: report.table_columns,
            tiktok_config: report.tiktok_config ?? null,
          }

          // Call API to create duplicate
          const { data } = await LensAPI.LensReport.create(duplicateReport)
          results.push(data)
        } catch (error) {
          console.error(`Failed to duplicate report ${report.id}:`, error)
          // Continue with other reports even if one fails
        }
      }

      return results
    },
    handleDeleteModalView (reportId) {
      this.reportToDelete = reportId
      this.show.deleteReportModal = !!reportId
      this.expandedReportOptions = null
    },
    handleBulkDelete () {
      if (this.selectedRows.length === 0) return

      // Show the delete confirmation modal
      this.show.deleteReportModal = true
      // Set reportToDelete to a special value indicating bulk delete
      this.reportToDelete = 'bulk'
    },
    async handleReportDelete () {
      this.loadingDelete = true
      try {
        if (this.reportToDelete === 'bulk') {
          // Bulk deletion
          const deletePromises = this.selectedRows.map(report =>
            LensAPI.LensReport.remove(report.id)
          )
          await Promise.all(deletePromises)

          // Filter out all deleted reports
          const deletedIds = this.selectedRows.map(report => report.id)
          this.reports = this.reports.filter(report => !deletedIds.includes(report.id))

          // Clear selection
          this.selectedRows = []
          await this.fetchSidebarLenses()
          this.$showAlert({
            type: 'success',
            message: `${deletedIds.length} reports successfully deleted`
          })
        } else {
          // Single report deletion (existing code)
          await LensAPI.LensReport.remove(this.reportToDelete)
          this.reports = this.reports.filter(report => report.id !== this.reportToDelete)
          await this.fetchSidebarLenses()
          this.$showAlert({
            type: 'success',
            message: 'Report successfully deleted'
          })
        }
      } catch (error) {
        console.error('Error deleting report(s):', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to delete report(s)'
        })
      } finally {
        this.loadingDelete = false
        this.handleDeleteModalView()
      }
    },
    // Checkbox functionality
    isRowSelected (reportId) {
      return this.selectedRows.some(row => row.id === reportId)
    },
    selectRow (report) {
      if (this.isRowSelected(report.id)) {
        // Remove from selected rows
        this.selectedRows = this.selectedRows.filter(row => row.id !== report.id)
      } else {
        // Add to selected rows
        this.selectedRows.push(report)
      }
      this.$emit('update:selectedReports', this.selectedRows)
    },
    toggleAllRows (shouldClear) {
      if (shouldClear) {
        // Clear all selections
        this.selectedRows = []
      } else {
        if (this.selectedRows.length === this.reports.length) {
          // If all are selected, deselect all
          this.selectedRows = []
        } else {
          // Select all reports
          this.selectedRows = [...this.reports]
        }
      }
      this.$emit('update:selectedReports', this.selectedRows)
    },
    showTooltip (reportId, show) {
      // Implement tooltip functionality if needed
      // This is a placeholder for future implementation
    },
    clearSelection () {
      this.selectedRows = []
      this.$emit('update:selectedReports', this.selectedRows)
    },
    updateReportsLocal (newReport) {
      this.reports.unshift(newReport)
      this.clearSelection()
    },
    navToReport (reportId) {
      this.$router.push({
        name: 'LensReport',
        params: {
          lensId: this.lensId,
          reportId
        }
      })
    },
    
    // ========= UI METHODS =========

    formatDate (dateMS) {
      const now = new Date()
      const date = new Date(dateMS)
      const diffTime = Math.abs(now - date)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
      const diffMonths = Math.floor(diffDays / 30)
      const diffYears = Math.floor(diffDays / 365)

      if (diffYears > 0) {
        return `${diffYears}y ago`
      } else if (diffMonths > 0) {
        return `${diffMonths}mo ago`
      } else if (diffDays > 0) {
        return `${diffDays}d ago`
      } else {
        const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
        if (diffHours > 0) {
          return `${diffHours}h ago`
        } else {
          const diffMinutes = Math.floor(diffTime / (1000 * 60))
          return diffMinutes > 0 ? `${diffMinutes}m ago` : 'Just now'
        }
      }
    },
    getReportAuthorAvatar (report) {
      return report?.created_by?.avatar || null
    },
    getReportAuthorName (report) {
      return report?.created_by?.name || 'Unknown'
    },
    getInitials (name) {
      return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2)
    },
    openNewReportModal (preSelectedType) {
      this.show.createReportModal = true
      this.preSelectedReportType = preSelectedType
    }
  }
}
</script>

<style scoped>
.report-grid-template {
  display: grid;
  grid-template-columns: 2fr 200px 300px 1fr;
}

/* Checkbox styles */
.checkbox-input {
  box-shadow: 0px 0px 0px 1px #09194821, 0px 1px 2px 0px #09194821;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background-color 150ms ease-in-out;
}

.checkbox-input:disabled {
  background-color: #ECEFF3;
  cursor: not-allowed;
}

.checkbox-input:checked {
  background-color: black;
}

.sidebar-offset {
  margin-left: 144px; /* Half of the sidebar width (288px ÷ 2) */
  transition: margin-left var(--collapse-transition-duration) ease-in-out;
}
.sidebar-offset.collapsed {
  margin-left: 38px; /* Half of the collapsed sidebar width (76px ÷ 2) */
}

.custom-cancel-button {
  border-radius: 6px;
  opacity: 0.68;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  box-shadow: 0px 1px 2px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.13), 0px 0px 0px 1px color(display-p3 0.0696 0.2157 0.4104 / 0.08);
  transition: all 50ms ease-in-out;
}

.custom-cancel-button:hover {
  opacity: 1;
  box-shadow: 0px 2px 4px 0px rgba(4, 26, 75, 0.2), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}

.custom-duplicate-button {
  border-radius: 6px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.08) 100%), rgba(255, 255, 255, 0.12);
  background: linear-gradient(180deg, color(display-p3 1 1 1 / 0.00) 0%, color(display-p3 1 1 1 / 0.08) 100%), color(display-p3 1 1 1 / 0.12);
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  box-shadow: 0px 1px 2px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.13), 0px 0px 0px 1px color(display-p3 0.0696 0.2157 0.4104 / 0.08);
  transition: all 50ms ease-in-out;
}

.custom-duplicate-button:hover {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.12) 100%), rgba(255, 255, 255, 0.16);
  box-shadow: 0px 2px 4px 0px rgba(4, 26, 75, 0.2), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}

.custom-button {
  border-radius: 6px;
  background: var(--Button-Secondary-Normal, linear-gradient(180deg, rgba(223, 225, 231, 0.08) 0%, rgba(223, 225, 231, 0.08) 100%), #FFF);
  background: var(--Button-Secondary-Normal, linear-gradient(180deg, color(display-p3 0.8757 0.8824 0.9043 / 0.00) 0%, color(display-p3 0.8757 0.8824 0.9043 / 0.08) 100%), color(display-p3 1 1 1));
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  box-shadow: 0px 1px 2px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.13), 0px 0px 0px 1px color(display-p3 0.0696 0.2157 0.4104 / 0.08);
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  outline: none;
  transition: all 50ms ease;
}

.custom-button:hover {
  background: var(--Button-Secondary-Hover, linear-gradient(180deg, rgba(223, 225, 231, 0.16) 0%, rgba(223, 225, 231, 0.16) 100%), #FFF);
  background: var(--Button-Secondary-Hover, linear-gradient(180deg, color(display-p3 0.8757 0.8824 0.9043 / 0.08) 0%, color(display-p3 0.8757 0.8824 0.9043 / 0.16) 100%), color(display-p3 1 1 1));
  box-shadow: 0px 1px 3px 0px rgba(4, 26, 75, 0.18), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
  box-shadow: 0px 1px 3px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.18), 0px 0px 0px 1px color(display-p3 0.0696 0.2157 0.4104 / 0.12);
}

.secondary-button {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}
.secondary-button:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.secondary-button {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}
.secondary-button:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.custom-delete-button {
  border-radius: 6px;
  background: linear-gradient(180deg, rgba(244, 0, 58, 0.08) 0%, rgba(244, 0, 58, 0.08) 100%), rgba(244, 0, 58, 0.12);
  background: linear-gradient(180deg, color(display-p3 0.8745 0.1098 0.2549 / 0.00) 0%, color(display-p3 0.8745 0.1098 0.2549 / 0.08) 100%), color(display-p3 0.8745 0.1098 0.2549 / 0.12);
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  box-shadow: 0px 1px 2px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.13), 0px 0px 0px 1px color(display-p3 0.0696 0.2157 0.4104 / 0.08);
}

.custom-delete-button:hover {
  background: linear-gradient(180deg, rgba(244, 0, 58, 0.16) 0%, rgba(244, 0, 58, 0.16) 100%), rgba(244, 0, 58, 0.16);
  background: linear-gradient(180deg, color(display-p3 0.8745 0.1098 0.2549 / 0.08) 0%, color(display-p3 0.8745 0.1098 0.2549 / 0.16) 100%), color(display-p3 0.8745 0.1098 0.2549 / 0.16);
  box-shadow: 0px 1px 3px 0px rgba(4, 26, 75, 0.18), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
  box-shadow: 0px 1px 3px 0px color(display-p3 0.0368 0.0984 0.2832 / 0.18), 0px 0px 0px 1px color(display-p3 0.0696 0.2157 0.4104 / 0.12);
}

.options-menu-button {
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.options-menu-button.active {
  background-color: #ECEFF3;
}
.options-menu-button.inactive:hover {
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.options-dropdown {
  position: absolute;
  right: calc(100% + 4px);
  top: 0;
  /* transform: translateY(-50%); */
  width: 115px;
  z-index: 500;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
